import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { CheckCircle2, ShieldCheck, FileText, Lock, MessageSquare, Phone, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * NexFlow Plumbing — Dedicated SMS Consent Page (Twilio-compliance ready).
 *
 * Standalone from Terms of Service and Privacy Policy. The verbatim consent
 * paragraph is fetched from /api/public/sms-consent-text so what is rendered
 * is provably identical to what is stored against every consent record.
 *
 * Captures (and the backend stores): full name, email, phone (E.164),
 * consent_version, consent_date, consent_time, consent_at (ISO), IP, UA, source.
 */
const SmsConsent = () => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const [policy, setPolicy] = useState({
    version: 'v2-2026-06-05',
    text: 'I agree to receive SMS messages from NexFlow Plumbing & Drain Services regarding appointment reminders, dispatch notifications, account updates, verification codes, and customer service communications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. Reply HELP for assistance. Consent is not a condition of purchase.',
    categories: [
      'appointment reminders', 'dispatch notifications', 'account updates',
      'verification codes', 'customer service communications',
    ],
  });

  useEffect(() => {
    axios.get(`${API}/public/sms-consent-text`).then(({ data }) => setPolicy(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) {
      setError('Please tick the SMS consent box above before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/sms-consent`, {
        ...form,
        consent: true,
        consent_version: policy.version,
        consent_source: 'sms_consent_page',
      });
      setDone(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not record consent — please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="sms-consent-page" className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-[#262626]">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-['Outfit'] text-white font-semibold tracking-wide">NEXFLOW PLUMBING &amp; DRAIN SERVICES</span>
          </Link>
          <Link to="/" className="text-[#A3A3A3] text-sm hover:text-white">Home</Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10 space-y-10">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-2">SMS COMMUNICATIONS</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold mb-3">SMS Consent</h1>
          <p className="text-[#A3A3A3] text-base leading-relaxed">
            This page collects your <span className="text-white font-medium">explicit, standalone consent</span> to
            receive SMS text messages from NexFlow Plumbing &amp; Drain Services. It is presented separately from our
            Terms of Service and Privacy Policy and you can use NexFlow services without opting in.
          </p>
        </div>

        {/* Banner: separation notice */}
        <div className="bg-[#141414] border border-[#D4AF37]/30 rounded-md p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
          <div className="text-sm text-[#A3A3A3] leading-relaxed">
            <p className="text-white font-medium mb-1">SMS consent is separate from our Terms &amp; Privacy.</p>
            <p>
              Accepting our <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms of Service</Link> or
              <Link to="/privacy" className="text-[#D4AF37] hover:underline ml-1">Privacy Policy</Link> does
              <span className="text-white font-medium"> not </span>opt you into SMS. You must tick the box below to
              receive text messages. <span className="text-white font-medium">Consent is not a condition of purchase.</span>
            </p>
          </div>
        </div>

        {/* What we'll send */}
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-3">WHAT WE&apos;LL TEXT YOU ABOUT</p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              { i: MessageSquare, t: 'Appointment reminders' },
              { i: Phone,          t: 'Dispatch notifications (technician on the way)' },
              { i: FileText,       t: 'Account updates' },
              { i: ShieldCheck,    t: 'Verification codes (sign-in / account security)' },
              { i: CheckCircle2,   t: 'Customer service communications' },
            ].map(({ i: Icon, t }, idx) => (
              <div key={idx} className="bg-[#141414] border border-[#262626] rounded-md p-3 flex items-start gap-2">
                <Icon className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                <p className="text-white text-sm">{t}</p>
              </div>
            ))}
          </div>
        </div>

        {done ? (
          <div data-testid="sms-consent-success" className="bg-[#141414] border border-[#D4AF37]/40 rounded-md p-6 text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-[#D4AF37] mx-auto" />
            <p className="text-white text-lg font-medium">Thanks — your SMS consent is recorded.</p>
            <p className="text-[#A3A3A3] text-sm" data-testid="sms-consent-version">
              Version <span className="text-[#D4AF37] font-mono">{done.consent_version}</span> · {new Date(done.consent_at).toLocaleString()}
            </p>
            <p className="text-[#A3A3A3] text-xs">
              Reply <span className="text-[#D4AF37] font-mono">STOP</span> at any time to opt out, or
              <span className="text-[#D4AF37] font-mono"> HELP</span> for assistance.
            </p>
            <Link to="/" className="inline-block text-[#D4AF37] text-sm hover:text-[#F3E5AB]">← Back to NexFlow Plumbing</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-5">
            {error && <div data-testid="sms-consent-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">{error}</div>}

            <div>
              <Label className="text-white">Full Name *</Label>
              <Input
                data-testid="sms-consent-name"
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                placeholder="Jane Doe"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Email *</Label>
                <Input
                  data-testid="sms-consent-email"
                  type="email"
                  required
                  inputMode="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label className="text-white">Mobile Phone *</Label>
                <Input
                  data-testid="sms-consent-phone"
                  type="tel"
                  required
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                  placeholder="(555) 555-5555"
                />
                <p className="text-[#525252] text-xs mt-1">US 10-digit or E.164 format.</p>
              </div>
            </div>

            {/* THE consent checkbox — verbatim language, unchecked by default, standalone */}
            <label className="flex items-start gap-3 p-4 rounded-md border-2 border-[#D4AF37]/40 bg-[#0A0A0A] hover:border-[#D4AF37] transition-colors cursor-pointer">
              <input
                data-testid="sms-consent-checkbox"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 accent-[#D4AF37] w-5 h-5 shrink-0"
              />
              <span data-testid="sms-consent-verbatim" className="text-white text-sm leading-relaxed">
                {policy.text}
              </span>
            </label>
            <p className="text-[#525252] text-xs text-center" data-testid="sms-consent-version-label">
              Consent version <span className="text-[#A3A3A3] font-mono">{policy.version}</span> · We capture the date,
              time, IP address, and user-agent of your submission.
            </p>

            <Button
              data-testid="sms-consent-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-40 text-base font-medium"
            >
              {submitting ? 'Submitting…' : 'Record my SMS consent'}
            </Button>
          </form>
        )}

        {/* STOP / HELP quick-reference */}
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-2">
          <p className="text-white font-medium flex items-center gap-2"><Lock className="w-4 h-4 text-[#D4AF37]" /> Compliance summary</p>
          <ul className="text-[#A3A3A3] text-sm space-y-1 list-disc pl-5">
            <li>Reply <span className="text-[#D4AF37] font-mono">STOP</span> to opt out at any time.</li>
            <li>Reply <span className="text-[#D4AF37] font-mono">HELP</span> for assistance.</li>
            <li>Message frequency varies based on your active appointments and account activity.</li>
            <li>Message and data rates may apply.</li>
            <li>Supported carriers include AT&amp;T, Verizon, T-Mobile, and most US carriers.</li>
            <li>We never sell, rent, or share your phone number with third parties.</li>
            <li><span className="text-white">Consent is not a condition of purchase.</span></li>
          </ul>
        </div>

        {/* Footer links */}
        <div className="border-t border-[#262626] pt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/terms" data-testid="terms-link" className="text-[#A3A3A3] hover:text-white">Terms of Service</Link>
          <Link to="/privacy" data-testid="privacy-link" className="text-[#A3A3A3] hover:text-white">Privacy Policy</Link>
          <Link to="/sms-consent" className="text-[#A3A3A3] hover:text-white">SMS Consent</Link>
          <Link to="/sms-compliance" className="text-[#A3A3A3] hover:text-white">SMS Compliance</Link>
          <a href="mailto:support@nexflowplumbing.com" className="text-[#A3A3A3] hover:text-white">support@nexflowplumbing.com</a>
        </div>
      </div>
    </div>
  );
};

export default SmsConsent;
